import React from 'react'

import { Badge, BadgeColors, formatMoneyCents, useIsMobile } from '@avantstay/backoffice-core'
import { formatDateRange } from '@avantstay/format-date-range'

import { mountRouteUrl, ROUTES } from '@routes'

import { OmniSearchBooking } from '../../../OmniSearch.types'
import * as S from './BookingDetail.styles'

export interface BookingDetailProps {
  booking: OmniSearchBooking
}

const renderBookingBadge = ({ booking }: BookingDetailProps): JSX.Element => {
  const { folio, hash } = booking

  const bookingTotal = folio?.summary.total || 0
  const bookingPaid = folio?.summary.paid.total || 0
  const bookingBalance = formatMoneyCents(folio?.summary.balance) || 0

  switch (true) {
    case !!(bookingTotal && bookingPaid) && bookingTotal === bookingPaid:
      return (
        <Badge dataCy={`omnisearch-paid-badge-${hash}`} badgeColor={BadgeColors.success}>
          Paid
        </Badge>
      )
    case bookingTotal > bookingPaid:
      return (
        <Badge dataCy={`omnisearch-pending-badge-${hash}`} badgeColor={BadgeColors.accent}>
          Balance &bull; {bookingBalance}
        </Badge>
      )
    case bookingTotal < bookingPaid:
      return (
        <Badge dataCy={`omnisearch-overpaid-badge-${hash}`} badgeColor={BadgeColors.failure}>
          Balance &bull; {bookingBalance}
        </Badge>
      )
    default:
      return <></>
  }
}

function BookingDetail({ booking }: BookingDetailProps): JSX.Element {
  const isMobile = useIsMobile()
  const { checkInTiming, checkOutTiming, guestName, hash, home } = booking

  if (isMobile) {
    return (
      <S.BookingDetailMobile to={mountRouteUrl(ROUTES.bookingInfo.path, { hash })}>
        <S.BookingDetailColumn>
          <S.DetailTitle>{guestName}</S.DetailTitle>
          <S.DetailSubtitle>
            {formatDateRange(checkInTiming.effectiveTime, checkOutTiming.effectiveTime)}
          </S.DetailSubtitle>
        </S.BookingDetailColumn>
        <S.BookingDetailColumn>
          <S.DetailTitle>
            {home.region?.name ?? ''} &bull; {home.name}
          </S.DetailTitle>
          <S.DetailSubtitle>{renderBookingBadge({ booking })}</S.DetailSubtitle>
        </S.BookingDetailColumn>
      </S.BookingDetailMobile>
    )
  }

  return (
    <S.BookingDetail data-cy={`omnisearch-result-${hash}`}>
      <S.BookingDetailColumn>
        <S.DetailTitle>{guestName}</S.DetailTitle>
        <S.DetailSubtitle>{hash}</S.DetailSubtitle>
      </S.BookingDetailColumn>
      <S.BookingDetailColumn>
        <S.DetailTitle>
          {formatDateRange(checkInTiming.effectiveTime, checkOutTiming.effectiveTime)}
        </S.DetailTitle>
        <S.DetailSubtitle>
          {home.region?.name ?? ''} &bull; {home.name}
        </S.DetailSubtitle>
      </S.BookingDetailColumn>
      <S.BookingPaymentColumn>{renderBookingBadge({ booking })}</S.BookingPaymentColumn>
    </S.BookingDetail>
  )
}

export default BookingDetail
